// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * Utilities for generating and managing accounting period lists.
 */
import {
  SavedAccountingPeriod,
  SavedAccountingPeriodActivityStatus,
} from '@/accounting/api/types';
import { hasLockedActivity } from '@/accounting/utilities/has-locked-activity';
import { DateTime, Zone } from 'luxon';

/** Number of past months to show in initial true-up period selector. */
const INITIAL_TRUE_UP_MONTHS_TO_SHOW = 12;

/**
 * Formats a period for display.
 * Format: "YYYY mmmm" (e.g., "2024 September")
 */
export function formatPeriod(period: SavedAccountingPeriod): string {
  const periodStartDate = new Date(period.year, period.month - 1, 1);
  return periodStartDate.toLocaleString('en-US', {
    month: 'long',
    year: 'numeric',
  });
}

/**
 * Creates an accounting period with no locked activity for a given year and month.
 */
export function createPeriod(
  year: number,
  month: number
): SavedAccountingPeriodActivityStatus {
  return {
    id: `generated-${String(year)}-${String(month)}`,
    key: `generated-${String(year)}-${String(month)}`,
    year,
    month,
    periodClose: null,
    initialTrueUps: [],
  };
}

/**
 * Generates a list of past months for the initial true-up period selector.
 *
 * Rules:
 * - Shows last 12 months (not including current month)
 * - No future months
 * - Ordered newest first (descending)
 * - Generated client-side (no backend periods required)
 * - The current month is the organization's, not the browser's
 *
 * @param organizationTimezone - The organization's timezone
 * @returns Array of periods for last 12 months, newest first
 */
export function generateInitialTrueUpPeriodList(
  organizationTimezone: Zone
): SavedAccountingPeriod[] {
  const now = DateTime.now().setZone(organizationTimezone);
  const periods: SavedAccountingPeriod[] = [];

  // Generate last 12 past months (starting from last month, going backwards)
  for (let i = 1; i <= INITIAL_TRUE_UP_MONTHS_TO_SHOW; i++) {
    const pastMonth = now.minus({ months: i });
    periods.push(createPeriod(pastMonth.year, pastMonth.month));
  }

  // Already in newest-first order since we started from last month
  return periods;
}

/**
 * Options for generating the period list.
 */
export interface GeneratePeriodListOptions {
  /**
   * The initial true-up period date. When provided, periods will start from this
   * date and end at the current month (no future months).
   * Format: { year: number, month: number }
   */
  initialTrueUpPeriod?: { year: number; month: number };
}

/**
 * Generates a complete list of accounting periods based on backend data.
 *
 * Rules when initialTrueUpPeriod is NOT provided (pre-initial-true-up):
 * - Always includes current month
 * - Always includes next month
 * - Goes back to oldest period from backend
 * - Fills in all months between oldest and now (even if no backend period exists)
 * - Does not show periods beyond next month
 * - Generated periods (not from backend) have no locked activity
 *
 * Rules when initialTrueUpPeriod IS provided (post-initial-true-up):
 * - Starts from the initial true-up period (first viewable period)
 * - Ends at current month (no future months)
 * - Fills in all months between initial true-up and current month
 * - Uses backend period data when available (for locked activity)
 *
 * The current month is the organization's, not the browser's.
 *
 * @param backendPeriods - Periods returned from the backend API
 * @param organizationTimezone - The organization's timezone
 * @param options - Optional configuration for period generation
 * @returns Complete list of periods with all gaps filled, newest first
 */
export function generatePeriodList(
  backendPeriods: SavedAccountingPeriodActivityStatus[],
  organizationTimezone: Zone,
  options?: GeneratePeriodListOptions
): SavedAccountingPeriodActivityStatus[] {
  const now = DateTime.now().setZone(organizationTimezone);
  const currentYear = now.year;
  const currentMonth = now.month;

  // Create a map of backend periods by year-month for quick lookup
  const backendPeriodMap = new Map<
    string,
    SavedAccountingPeriodActivityStatus
  >();
  for (const period of backendPeriods) {
    const key = `${String(period.year)}-${String(period.month)}`;
    backendPeriodMap.set(key, period);
  }

  // Determine start and end periods based on whether initial true-up exists
  let startYear: number;
  let startMonth: number;
  let endYear: number;
  let endMonth: number;

  if (options?.initialTrueUpPeriod) {
    // Post-initial-true-up: start from initial true-up, end at current month
    startYear = options.initialTrueUpPeriod.year;
    startMonth = options.initialTrueUpPeriod.month;
    endYear = currentYear;
    endMonth = currentMonth;
  } else {
    // Pre-initial-true-up: use original logic (oldest backend period to next month)
    const nextMonthDate = now.plus({ months: 1 });
    endYear = nextMonthDate.year;
    endMonth = nextMonthDate.month;

    // Find oldest period from backend, defaulting to current month
    if (backendPeriods.length > 0) {
      const oldest = backendPeriods.reduce((oldest, period) => {
        if (
          period.year < oldest.year ||
          (period.year === oldest.year && period.month < oldest.month)
        ) {
          return period;
        }
        return oldest;
      });
      startYear = oldest.year;
      startMonth = oldest.month;
    } else {
      startYear = currentYear;
      startMonth = currentMonth;
    }
  }

  // Generate all periods from start to end
  const periods: SavedAccountingPeriodActivityStatus[] = [];
  let year = startYear;
  let month = startMonth;

  while (year < endYear || (year === endYear && month <= endMonth)) {
    const key = `${String(year)}-${String(month)}`;
    const backendPeriod = backendPeriodMap.get(key);

    if (backendPeriod) {
      // Use backend period (has real ID and locked activity)
      periods.push(backendPeriod);
    } else {
      // Generate period for gap (no locked activity)
      periods.push(createPeriod(year, month));
    }

    // Move to next month
    month++;
    if (month > 12) {
      month = 1;
      year++;
    }
  }

  // Sort periods by year and month (newest first)
  periods.sort((b, a) => {
    if (a.year === b.year) {
      return a.month - b.month;
    }
    return a.year - b.year;
  });
  return periods;
}

/**
 * Finds the default period to select from a list of periods.
 *
 * Rules:
 * - Selects the oldest unlocked period (the next period to close after the
 *   most recent locked period)
 * - If all periods are locked, selects the most recent (newest) period
 *
 * Note: The periods array is expected to be sorted with newest periods first.
 * To find the oldest unlocked, we search from the end of the array.
 *
 * @param periods - List of accounting periods (sorted newest first)
 * @returns The period to select by default, or null if list is empty
 */
export function findDefaultPeriod(
  periods: SavedAccountingPeriodActivityStatus[]
): SavedAccountingPeriodActivityStatus | null {
  if (periods.length === 0) {
    return null;
  }

  // Find oldest unlocked period by searching from the end
  // (since periods are sorted newest first, oldest is at the end)
  for (let i = periods.length - 1; i >= 0; i--) {
    if (!hasLockedActivity(periods[i])) {
      return periods[i];
    }
  }

  // If all periods are locked, return the newest period (first in array)
  return periods[0];
}

/**
 * Finds the most recently locked period from a list of periods.
 *
 * Rules:
 * - Selects the newest locked period
 * - If no periods are locked, returns null
 *
 * Note: The periods array is expected to be sorted with newest periods first.
 *
 * @param periods - List of accounting periods (sorted newest first)
 * @returns The most recently locked period, or null if none are locked
 */
export function findMostRecentlyLockedPeriod(
  periods: SavedAccountingPeriodActivityStatus[]
): SavedAccountingPeriodActivityStatus | null {
  if (periods.length === 0) {
    return null;
  }

  // Find newest locked period by searching from the start
  // (since periods are sorted newest first)
  for (const period of periods) {
    if (hasLockedActivity(period)) {
      return period;
    }
  }

  // No locked periods found
  return null;
}
